import * as Yup from "yup";
import { SUPER_ADMIN_ROLE } from "../../../utils/constants";
import {
  emailValidationRegex,
  websiteUrlRegex,
} from "../../../constants/regexStrings";
import phoneValidation from "../../../common/components/phone-number-input-component/phone-validation";
import _ from "lodash";
import { isOnlyCountryCode } from "../../../utils/utils";

export interface User {
  firstName: string;
  lastName: string;
  email: string;
  phoneNumber: any;
  roleId: number | null;
  isPrimaryUser: boolean;
}

interface Address {
  streetLine1: string;
  streetLine2: string;
  city: string;
  state: string;
  country: string;
  postcode: string;
}

export interface CompanyFormValues {
  id: undefined;
  companyName: string;
  primaryColor: string;
  accentColor: string;
  companyWebsite: string;
  phoneNumber: any;
  email: string;
  logo_url: any;
  employeeCount: number | null;
  timezone: string | null;
  subscriptionPlanId: any;
  users: User[];
  address: Address;
  billingAddress: Address;
  products?: any[];
}

export const userDetails: User = {
  firstName: "",
  lastName: "",
  email: "",
  phoneNumber: "",
  roleId: SUPER_ADMIN_ROLE,
  isPrimaryUser: false,
};

export const getInitialValues = (rowData?: any): CompanyFormValues => {
  let onboardingUser;

  try {
    onboardingUser = rowData?.TenantPrimaryUsers;
  } catch (error) {
    console.error("Failed to parse onboarding_users:", error);
    onboardingUser = {};
  }
  return {
    id: rowData?.id ?? null,
    companyName: rowData?.company_name ?? "",
    primaryColor: "",
    accentColor: "",
    companyWebsite: "",
    phoneNumber: "",
    logo_url: null,
    employeeCount: null,
    timezone: "",
    subscriptionPlanId: null,
    email: "",
    users: [
      {
        ...userDetails,
        firstName: onboardingUser?.first_name ?? "",
        lastName: onboardingUser?.last_name ?? "",
        email: onboardingUser?.email ?? "",
        phoneNumber: onboardingUser?.phone_number ?? "",
        isPrimaryUser: true,
      },
    ],
    address: {
      streetLine1: "",
      streetLine2: "",
      city: "",
      state: "",
      country: "",
      postcode: "",
    },
    billingAddress: {
      streetLine1: "",
      streetLine2: "",
      city: "",
      state: "",
      country: "",
      postcode: "",
    },
  };
};
const FILE_SIZE = 110 * 1024 * 1024;
const SUPPORTED_FORMATS = [
  "image/jpg",
  "image/jpeg",
  "image/png",
  "image/svg+xml",
  "image/webp",
];

export const validationSchema = [
  Yup.object().shape({
    companyName: Yup.string().required("Company name is required"),
    primaryColor: Yup.string(),
    accentColor: Yup.string(),
    companyWebsite: Yup.string()
      .matches(websiteUrlRegex, "Enter a valid website URL")
      .nullable(),
    phoneNumber: Yup.string()
      .required("Phone number is required")
      .test(
        "valid-phone-check",
        "Please enter a valid phone number",
        function (value) {
          if (!value) return false;

          if (isOnlyCountryCode(value)) {
            return this.createError({
              message: "Phone number is required",
            });
          }

          if (!phoneValidation(value)) {
            return this.createError({
              message: "Please enter a valid phone number",
            });
          }

          return true;
        }
      ),
    email: Yup.string()
      .matches(emailValidationRegex, "Invalid email format")
      .required("Email is required"),
    logo_url: Yup.mixed()
      .nullable()
      .required("Logo is required")
      .test(
        "fileFormat",
        "Only JPG, JPEG, PNG, SVG and WEBP files are allowed",
        (value: any) => {
          return value && SUPPORTED_FORMATS.includes(value?.type);
        }
      )
      .test("fileSize", "File size must be less than 100MB", (value: any) => {
        return value && value?.size <= FILE_SIZE;
      }),
    employeeCount: Yup.number()
      .min(1, "Employee count must be greater than 0")
      .nullable(),
    timezone: Yup.string().required("Timezone is required"),
    users: Yup.array().of(
      Yup.object().shape({
        firstName: Yup.string().required("First name is required"),
        lastName: Yup.string().required("Last name is required"),
        email: Yup.string()
          .matches(emailValidationRegex, "Please enter a valid Email address")
          .required("Email is required"),
        phoneNumber: Yup.string()
          .required("Phone number is required")
          .test(
            "valid-phone-check",
            "Please enter a valid phone number",
            function (value) {
              if (!value) return false;

              if (isOnlyCountryCode(value)) {
                return this.createError({
                  message: "Phone number is required",
                });
              }

              if (!phoneValidation(value)) {
                return this.createError({
                  message: "Please enter a valid phone number",
                });
              }

              return true;
            }
          ),
        roleId: Yup.string().nullable(),
        isPrimaryUser: Yup.boolean(),
      })
    ),
    address: Yup.object().shape({
      streetLine1: Yup.string().nullable(),
      streetLine2: Yup.string().nullable(),
      city: Yup.string().nullable(),
      state: Yup.string().nullable(),
      country: Yup.string().nullable(),
      postcode: Yup.string().nullable(),
    }),

    billingAddress: Yup.object().shape({
      streetLine1: Yup.string().nullable(),
      streetLine2: Yup.string().nullable(),
      city: Yup.string().nullable(),
      state: Yup.string().nullable(),
      country: Yup.string().nullable(),
      postcode: Yup.string().nullable(),
    }),
  }),
  Yup.object().shape({
    subscriptionPlanId: Yup.object()
      .nullable()
      .required("Subscription plan is required"),
  }),
  Yup.object().shape({
    users: Yup.array().of(
      Yup.object().shape({
        firstName: Yup.string().required("First name is required"),
        lastName: Yup.string().required("Last name is required"),
        email: Yup.string()
          .matches(emailValidationRegex, "Please enter a valid Email address")
          .required("Email is required"),
        phoneNumber: Yup.string()
          .required("Phone number is required")
          .test(
            "valid-phone-check",
            "Please enter a valid phone number",
            function (value) {
              if (!value) return false;

              if (isOnlyCountryCode(value)) {
                return this.createError({
                  message: "Phone number is required",
                });
              }

              if (!phoneValidation(value)) {
                return this.createError({
                  message: "Please enter a valid phone number",
                });
              }

              return true;
            }
          ),
        roleId: Yup.string().required("Role is required"),
        isPrimaryUser: Yup.boolean(),
      })
    ),
  }),
];

export const steps: string[] = [
  "Basic Details",
  "Subscription Plans",
  "Add Users",
];
export const nextButtonLabel = { 0: "Choose Plan", 1: "Add User", 2: "Submit" };

export const markAllFieldsTouched = <T extends Record<string, any>>(
  values: T,
  touched: Record<string, any> = {}
): Record<string, any> => {
  Object.keys(values).forEach((key) => {
    const value = values[key];
    if (typeof value === "object" && value !== null && !Array.isArray(value)) {
      touched[key] = markAllFieldsTouched(value);
    } else if (Array.isArray(value)) {
      touched[key] = value.map((item) => markAllFieldsTouched(item));
    } else {
      touched[key] = true;
    }
  });
  return touched;
};

export const headers = [
  {
    label: "",
    key: "delete",
    fixedColumnWidth: "60px",
    className: "delete-icon",
    isDisabled: (row: any) => {
      return !!row?.isPrimaryUser;
    },
  },
  {
    label: "First Name",
    key: "firstName",
    fieldType: "text",
    fieldName: "firstName",
    isEditable: true,
    maxLength: 50,
    minLength: 3,
    isAlphaNumeric: true,
    isDisabled: (row: any) => {
      return !!row?.isPrimaryUser || !!row?.id;
    },
  },
  {
    label: "Last Name",
    key: "lastName",
    fieldType: "text",
    fieldName: "lastName",
    isEditable: true,
    maxLength: 50,
    minLength: 3,
    isAlphaNumeric: true,
    isDisabled: (row: any) => {
      return !!row?.isPrimaryUser || !!row?.id;
    },
  },
  {
    label: "Email",
    key: "email",
    fieldType: "email",
    fieldName: "email",
    isEditable: true,
    maxLength: 150,
    isDisabled: (row: any) => {
      return !!row?.isPrimaryUser;
    },
  },
  {
    label: "Phone",
    key: "phoneNumber",
    fieldType: "text",
    fieldName: "phoneNumber",
    isEditable: true,
    maxLength: 15,
    isPhonenumber: true,
    showPhoneExampleFormat: false,
    isDisabled: (row: any) => {
      return !!row?.isPrimaryUser;
    },
  },
  {
    label: "Role",
    key: "roleId",
    isEditable: true,
    isDisabled: (row: any) => {
      return !!row?.isPrimaryUser;
    },
  },
];

type UserError = Record<string, string | undefined>;

const checkIfAnyUserFieldEmpty = (
  users: { [key: string]: any }[]
): string | null => {
  for (const user of users) {
    const hasEmpty = _.some(user, (value) => {
      return (
        value === null ||
        value === undefined ||
        (typeof value === "string" && value.trim() === "")
      );
    });

    if (hasEmpty) {
      return "You're almost there! Please make sure all fields are filled out.";
    }
  }

  return null;
};
export const getUserErrorMessage = (
  userErrors: unknown,
  userValues: any,
  requiredFieldCount = 5,
  isSubmitValidation = false
): string | null | boolean => {
  if (!Array.isArray(userErrors)) return null;
  const userArrayEmptyValueMsg = checkIfAnyUserFieldEmpty(userValues);
  if (userArrayEmptyValueMsg && isSubmitValidation) {
    return userArrayEmptyValueMsg;
  }
  if (userArrayEmptyValueMsg && !isSubmitValidation) {
    return true;
  }
  for (let index = 0; index < userErrors.length; index++) {
    const userError = userErrors[index];

    if (userError && typeof userError === "object") {
      const entries = Object.entries(userError as UserError);
      const isAllFieldsMissing = entries.length === requiredFieldCount;

      const errorMessages = entries
        .filter(([, msg]) => typeof msg === "string")
        .map(([, msg]) => msg)
        .join(", ");

      if (isAllFieldsMissing && !isSubmitValidation) {
        return true;
      }
      if (errorMessages) {
        return errorMessages ? `Row ${index + 1}: ${errorMessages}` : "";
      }
    }
  }

  return null;
};

const isNumeric = (value: string): boolean => {
  return !isNaN(Number(value)) && isFinite(Number(value));
};

const sortByPrice = (items: any) => {
  return items.sort((a: any, b: any) => {
    const aIsNum = isNumeric(a.price);
    const bIsNum = isNumeric(b.price);

    if (aIsNum && bIsNum) {
      return Number(a.price) - Number(b.price);
    }
    if (aIsNum) return -1;
    if (bIsNum) return 1;
    return 0;
  });
};

export const filterSubscriptionPlans = (
  subscriptionsPlans: any,
  activePlanDetails?: any
) => {
  const monthly: any = [];
  const annually: any = [];
  const noCost: any = [];
  const customMonthly: any[] = [];
  const customAnnual: any[] = [];
  let activeTab = 0;
  const activePlans = subscriptionsPlans?.filter((plan: any) => plan.active);
  if (activePlans?.length) {
    activePlans?.forEach((item: any) => {
      if (item.plan_category === "CUSTOM" && item.active) {
        customMonthly.push({
          ...item,
          price: item.monthly_price,
          plan_type: "monthly",
        });

        customAnnual.push({
          ...item,
          price: item.annual_price,
          plan_type: "annual",
        });
      } else if (item?.plan_category === "PAID") {
        monthly.push({
          ...item,
          price: item?.monthly_price,
          plan_type: "monthly",
        });
        annually.push({
          ...item,
          price: item?.annual_price,
          plan_type: "annual",
        });
        if (
          activePlanDetails &&
          activePlanDetails?.id === item?.id &&
          activePlanDetails?.plan_type === "annual"
        ) {
          activeTab = 1;
        }
      } else {
        noCost.push({ ...item, price: "0" });
        if (activePlanDetails && activePlanDetails?.id === item?.id) {
          activeTab = 2;
        }
      }
    });
  }
  return {
    monthly: sortByPrice(monthly),
    annually: sortByPrice(annually),
    noCost,
    activeTab,
    customMonthly,
    customAnnual,
  };
};

export const handleButtonText = (plan: any, activePlan: any) => {
  const activePrice = Number(activePlan?.price);
  const planPrice = Number(plan?.price);
  if (!activePlan?.subscription_plan_id) {
    return "Choose plan";
  }

  const isDowngrade =
    isNaN(activePrice) || Number(planPrice) < Number(activePrice);

  if (activePlan?.plan_category === "NO_COST") {
    return "Upgrade";
  }
  if (plan?.plan_category === "NO_COST") {
    return "Downgrade";
  }
  return isDowngrade ? "Downgrade" : "Upgrade";
};
